import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@strivelabs/ui-core';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ChartWrapper } from './ChartWrapper';
import { LineChart } from './LineChart';
import { AreaChart } from './AreaChart';
import { BarChart } from './BarChart';
import { PieChart } from './PieChart';
import { RadialChart } from './RadialChart';
import { ScatterChart } from './ScatterChart';
import type { LineChartProps } from './LineChart';
import type { AreaChartProps } from './AreaChart';
import type { BarChartProps } from './BarChart';
import type { PieChartProps } from './PieChart';
import type { RadialChartProps } from './RadialChart';
import type { ScatterChartProps } from './ScatterChart';

/** Props to omit from inner chart types — TabbedChart owns these */
type OmitWrapperProps = 'title' | 'description' | 'expandable' | 'expandedHeight' | 'className';

export type TabbedChartTab =
  | { id: string; label: string; chartType: 'line'; chartProps: Omit<LineChartProps, OmitWrapperProps> }
  | { id: string; label: string; chartType: 'area'; chartProps: Omit<AreaChartProps, OmitWrapperProps> }
  | { id: string; label: string; chartType: 'bar'; chartProps: Omit<BarChartProps, OmitWrapperProps> }
  | { id: string; label: string; chartType: 'pie'; chartProps: Omit<PieChartProps, OmitWrapperProps> }
  | { id: string; label: string; chartType: 'radial'; chartProps: Omit<RadialChartProps, OmitWrapperProps> }
  | { id: string; label: string; chartType: 'scatter'; chartProps: Omit<ScatterChartProps, OmitWrapperProps> };

export interface TabbedChartProps {
  /** Tab definitions — each tab specifies the chart type and its data/config */
  tabs: TabbedChartTab[];
  /** ID of the initially selected tab. Defaults to the first tab. */
  defaultTabId?: string;
  /** Card title rendered above the tab bar */
  title?: string;
  /** Card description rendered below the title */
  description?: string;
  /** Chart height in pixels (default: 300) */
  height?: number;
  /** Additional class name for the outer card */
  className?: string;
  /** Show the expand-to-fullscreen button (default: true) */
  expandable?: boolean;
  /** Chart height inside the expanded dialog (default: 500) */
  expandedHeight?: number;
  /** @internal Set by ChartWrapper when rendering in expanded dialog */
  isExpanded?: boolean;
}

// Strips the card appearance from inner charts so they sit flush inside TabbedChart's own card
const BARE_CARD_CLASS = 'border-0 p-0 rounded-none bg-transparent shadow-none';

function renderActiveChart(
  tab: TabbedChartTab,
  height: number,
  isExpanded: boolean
): React.ReactNode {
  const shared = {
    height,
    expandable: false as const,
    isExpanded,
    className: BARE_CARD_CLASS,
  };

  switch (tab.chartType) {
    case 'line':
      return <LineChart {...tab.chartProps} {...shared} />;
    case 'area':
      return <AreaChart {...tab.chartProps} {...shared} />;
    case 'bar':
      return <BarChart {...tab.chartProps} {...shared} />;
    case 'pie':
      return <PieChart {...tab.chartProps} {...shared} />;
    case 'radial':
      return <RadialChart {...tab.chartProps} {...shared} />;
    case 'scatter':
      return <ScatterChart {...tab.chartProps} {...shared} />;
  }
}

export const TabbedChart: React.FC<TabbedChartProps> = ({
  tabs,
  defaultTabId,
  title,
  description,
  height = 300,
  className,
  expandable = true,
  expandedHeight = 500,
  isExpanded = false,
}) => {
  const [activeTabId, setActiveTabId] = useState<string>(defaultTabId ?? tabs[0]?.id ?? '');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollState, tabs]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.6;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  const activeTab = tabs.find((t) => t.id === activeTabId) ?? tabs[0];
  const chartHeight = isExpanded ? expandedHeight : height;

  return (
    <ChartWrapper
      title={title}
      description={description}
      className={className}
      expandable={expandable}
      expandedHeight={expandedHeight}
      chartComponent={TabbedChart}
      chartProps={{
        tabs,
        defaultTabId: activeTabId, // preserve active tab when re-rendered in the dialog
        height,
      }}
    >
      <Tabs value={activeTabId} onValueChange={setActiveTabId}>
        <div className="relative flex items-center">
          {canScrollLeft && (
            <button
              type="button"
              aria-label="Scroll tabs left"
              onClick={() => scroll('left')}
              className="absolute left-0 z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-background shadow-sm hover:bg-muted"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}

          <div
            ref={scrollRef}
            onScroll={updateScrollState}
            className={`overflow-x-auto scrollbar-none ${canScrollLeft ? 'pl-9' : ''} ${canScrollRight ? 'pr-9' : ''}`}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <TabsList className="border border-border shadow-sm w-max">
              {tabs.map((tab) => (
                <TabsTrigger key={tab.id} value={tab.id}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {canScrollRight && (
            <button
              type="button"
              aria-label="Scroll tabs right"
              onClick={() => scroll('right')}
              className="absolute right-0 z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-background shadow-sm hover:bg-muted"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Active chart — rendered without its own card shell */}
        <div className="mt-6">
          {activeTab && renderActiveChart(activeTab, chartHeight, isExpanded)}
        </div>
      </Tabs>
    </ChartWrapper>
  );
};
